<!-- PaymentForm.vue -->
<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { formatDateTimeForDisplay } from "@/utils/dateUtils";

// Stores
import { usePaymentStore } from "@/stores/usePaymentStore";
import { useUserStore } from "@/stores/useUserStore";

// Modais
import AddPaymentForm from "./AddPaymentForm.vue";
import EditPaymentForm from "./EditPaymentForm.vue";
import DeletePaymentForm from "./DeletePaymentForm.vue";
import BatchDeletePaymentForm from "./BatchDeletePaymentForm.vue";
import PaymentCardModal from "./PaymentCardModal.vue";

// Filtros
import SearchPaymentInput from "./SearchPaymentInput.vue";

// Paginação
import PaginationButtons from "../Generics/PaginationButtons.vue";
import PaginationInfo from "../Generics/PaginationInfo.vue";

const paymentStore = usePaymentStore();
const userStore = useUserStore();

// Sys: usa o mesmo padrão de SubscriptionForm
const isSys = computed(() => userStore.currentUser?.userName === "Sys");

// ID do usuário logado
const callerId = computed(() =>
  userStore.currentUser?.userId != null
    ? String(userStore.currentUser.userId)
    : null
);

// ─── Modais ──────────────────────────────────────────────────────────────────
const showAddModal = ref(false);
const showEditModal = ref(false);
const showDeleteModal = ref(false);
const showBatchDeleteModal = ref(false);
const showCardModal = ref(false);

const selectedPayment = ref(null);
const selectedCardPaymentId = ref(null);

function handleOpenCard(payment) {
  selectedCardPaymentId.value = payment.paymentId;
  showCardModal.value = true;
}

// ─── Paginação ───────────────────────────────────────────────────────────────
const currentPage = ref(1);
const pageSize = ref(10);

// ✅ padrão TagForm.vue
const isLoadingTable = ref(false);

// ─── Dropdowns ───────────────────────────────────────────────────────────────
const openDropdownId = ref(null);
const isBatchDropdownOpen = ref(false);
const batchDropdownRef = ref(null);

// ─── Toast ───────────────────────────────────────────────────────────────────
const showToast = ref(false);
const toastMessage = ref("");
const toastType = ref("error");
const toastTimeoutId = ref(null);

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, 3000);
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

// ─── Normalização de status ───────────────────────────────────────────────────
function normalizePaymentStatus(status) {
  if (status === undefined || status === null) return "none";
  const s = String(status).trim().toLowerCase();
  const map = {
    none: "none",
    pending: "pending",
    approved: "approved",
    rejected: "rejected",
    cancelled: "cancelled",
    refunded: "refunded",
    0: "none",
    1: "pending",
    2: "approved",
    3: "rejected",
    4: "cancelled",
    5: "refunded",
  };
  return map[s] ?? "none";
}

function getPaymentStatusLabel(status) {
  const labels = {
    none: "—",
    pending: "Pendente",
    approved: "Aprovado",
    rejected: "Rejeitado",
    cancelled: "Cancelado",
    refunded: "Estornado",
  };
  return labels[normalizePaymentStatus(status)] ?? "—";
}

function getPaymentStatusClass(status) {
  const classes = {
    none: "status-tag status-gray",
    pending: "status-tag status-yellow",
    approved: "status-tag status-green",
    rejected: "status-tag status-red",
    cancelled: "status-tag status-red",
    refunded: "status-tag status-purple",
  };
  return classes[normalizePaymentStatus(status)] ?? "status-tag status-gray";
}

// ─── Permissões ───────────────────────────────────────────────────────────────
function canEdit() {
  return true;
}
function canDelete() {
  return true;
}

const anyEditableOnPage = computed(() =>
  paymentStore.filteredPayments.some(() => canEdit())
);

const isAllSelectedOnPage = computed({
  get: () => {
    const editableIds = paymentStore.filteredPayments
      .filter(() => canEdit())
      .map((p) => p.paymentId);
    if (editableIds.length === 0) return false;
    return editableIds.every((id) =>
      paymentStore.selectedPaymentIds.includes(id)
    );
  },
  set: (value) => {
    const editableIds = paymentStore.filteredPayments
      .filter(() => canEdit())
      .map((p) => p.paymentId);

    const allIdsOnPage = paymentStore.filteredPayments.map((p) => p.paymentId);

    if (value) {
      editableIds.forEach((id) => {
        if (!paymentStore.selectedPaymentIds.includes(id))
          paymentStore.selectedPaymentIds.push(id);
      });
    } else {
      paymentStore.selectedPaymentIds = paymentStore.selectedPaymentIds.filter(
        (id) => !allIdsOnPage.includes(id)
      );
    }
  },
});

const canBatchActions = computed(
  () =>
    Array.isArray(paymentStore.selectedPaymentIds) &&
    paymentStore.selectedPaymentIds.length > 0
);

// ─── Ações de linha ───────────────────────────────────────────────────────────
function toggleRowDropdown(id) {
  openDropdownId.value = openDropdownId.value === id ? null : id;
}

function handleEdit(payment) {
  selectedPayment.value = payment;
  showEditModal.value = true;
  openDropdownId.value = null;
}

function handleView(payment) {
  handleOpenCard(payment);
  openDropdownId.value = null;
}

function handleDelete(payment) {
  selectedPayment.value = payment;
  showDeleteModal.value = true;
  openDropdownId.value = null;
}

function toggleSelection(id) {
  paymentStore.toggleSelection(id);
}

// ─── Fetch ────────────────────────────────────────────────────────────────────
async function fetchPayments() {
  try {
    isLoadingTable.value = true;

    if (!isSys.value && callerId.value) {
      paymentStore.filters.userId = callerId.value;
    }

    await paymentStore.fetchPaymentsPaged(
      currentPage.value,
      pageSize.value,
      paymentStore.userIdFilter
    );
  } catch (error) {
    console.error("❌ Erro ao buscar payments:", error?.message);
    showErrorToast("Erro ao carregar payments.");
  } finally {
    isLoadingTable.value = false;
  }
}

watch(currentPage, fetchPayments);

// ─── Batch delete ─────────────────────────────────────────────────────────────
function tryOpenBatchDeleteModal() {
  if (
    !Array.isArray(paymentStore.selectedPaymentIds) ||
    paymentStore.selectedPaymentIds.length === 0
  ) {
    showErrorToast("Nenhum payment selecionado.");
    isBatchDropdownOpen.value = false;
    return;
  }
  showBatchDeleteModal.value = true;
  isBatchDropdownOpen.value = false;
}

// ─── Handler pós-mudança ──────────────────────────────────────────────────────
async function handlePaymentsChanged(options = {}) {
  const {
    goToFirstPage = true,
    closeAdd = true,
    closeEdit = true,
    closeDelete = true,
    closeBatchDelete = true,
    clearSelection = false,
    successMessage = null,
  } = options;

  if (closeAdd) showAddModal.value = false;
  if (closeEdit) showEditModal.value = false;
  if (closeDelete) showDeleteModal.value = false;
  if (closeBatchDelete) showBatchDeleteModal.value = false;

  if (clearSelection) paymentStore.clearSelection();
  if (successMessage) showSuccessToast(successMessage);

  if (goToFirstPage) {
    if (currentPage.value !== 1) {
      currentPage.value = 1;
      return;
    }
    await fetchPayments();
    return;
  }
  await fetchPayments();
}

// ─── Click fora → fecha dropdowns ────────────────────────────────────────────
function handleClickOutside(event) {
  if (
    batchDropdownRef.value &&
    !batchDropdownRef.value.contains(event.target)
  ) {
    isBatchDropdownOpen.value = false;
  }
  if (openDropdownId.value && !event.target.closest(".action-cell-container")) {
    openDropdownId.value = null;
  }
}

onMounted(() => {
  document.addEventListener("click", handleClickOutside);

  if (!isSys.value && callerId.value) {
    paymentStore.filters.userId = callerId.value;
    paymentStore.userIdFilter = Number(callerId.value);
  }

  fetchPayments();
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  isLoadingTable.value = false;
});
</script>

<template>
  <div class="w-full h-full">
    <div class="main-container">
      <div class="main-header">
        <div class="flex flex-col gap-0">
          <div class="text-xl font-bold">Pagamentos</div>
          <div class="text-sm mt-1 text-gray-700 ">
            <template v-if="isSys">
              Gerencie os registros de pagamento do sistema, acompanhe status,
              métodos, valores e cupons aplicados em cada transação.
            </template>
            <template v-else>
              Acompanhe seus registros de pagamento, status, métodos, valores e
              cupons aplicados em cada transação.
            </template>
          </div>
        </div>
      </div>

      <div class="action-bar action-bar-stacked">
        <div class="w-full">
          <SearchPaymentInput :is-sys="isSys" />
        </div>

        <div
          class="w-full flex flex-row items-center justify-end space-x-3 flex-shrink-0"
        >
          <template v-if="isSys">
            <button @click="showAddModal = true" class="btn btn-primary">
              <svg
                class="h-4 w-4 mr-1.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  clip-rule="evenodd"
                  fill-rule="evenodd"
                  d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                />
              </svg>
              Adicionar Pagamento
            </button>

            <div ref="batchDropdownRef" class="relative">
              <button
                @click="isBatchDropdownOpen = !isBatchDropdownOpen"
                class="btn btn-batch"
                :disabled="!canBatchActions"
              >
                Ações em lote
                <svg
                  class="-mr-1 ml-1.5 w-5 h-5 batch-chevron"
                  :class="{ open: isBatchDropdownOpen }"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    clip-rule="evenodd"
                    fill-rule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  />
                </svg>
              </button>

              <div v-if="isBatchDropdownOpen" class="dropdown-menu">
                <div class="py-1">
                  <a
                    href="#"
                    role="button"
                    @click.prevent="tryOpenBatchDeleteModal"
                    class="dropdown-item danger-item"
                  >
                    <i class="fas fa-trash-alt"></i>
                    <span>Apagar selecionados</span>
                  </a>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <div class="table-wrapper">
        <table class="main-table">
          <thead>
            <tr>
              <th style="width: 4%">
                <input
                  id="checkbox-all"
                  type="checkbox"
                  v-model="isAllSelectedOnPage"
                  class="checkbox-style"
                  :disabled="!anyEditableOnPage"
                  :class="{ 'disabled-item': !anyEditableOnPage }"
                />
              </th>
              <th style="width: 60px">Ações</th>
              <th style="width: 5%">ID</th>
              <th style="width: 6%">Assinatura</th>
              <th style="width: 6%">Usuário</th>
              <th style="width: 9%">Método</th>
              <th style="width: 10%">MP Pay ID</th>
              <th style="width: 8%">Status</th>
              <th style="width: 7%">Base (R$)</th>
              <th style="width: 7%">Desconto</th>
              <th style="width: 7%">Final (R$)</th>
              <th style="width: 8%">Cupom</th>
              <th style="width: 11%">Criado em</th>
              <th style="width: 12%">Detalhe</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="payment in !isLoadingTable ? paymentStore.filteredPayments : []"
              :key="payment.paymentId"
              class="clickable-row"
              @click="handleOpenCard(payment)"
            >
              <td @click.stop>
                <input
                  type="checkbox"
                  :value="payment.paymentId"
                  :checked="
                    paymentStore.selectedPaymentIds?.includes(payment.paymentId)
                  "
                  @change="toggleSelection(payment.paymentId)"
                  class="checkbox-style"
                  :disabled="!canEdit()"
                  :class="{ 'disabled-item': !canEdit() }"
                />
              </td>

              <td @click.stop class="action-cell-container">
                <div class="row-actions">
                  <button
                    @click="toggleRowDropdown(payment.paymentId)"
                    class="btn-icon btn-soft-brand"
                    title="Menu de Ações"
                  >
                    <i class="fas fa-ellipsis-h fa-lg"></i>
                  </button>
                </div>

                <div
                  v-if="openDropdownId === payment.paymentId"
                  class="row-dropdown-menu"
                >
                  <ul>
                    <li
                      v-if="isSys"
                      :class="{ 'disabled-item': !canEdit() }"
                      @click="canEdit() && handleEdit(payment)"
                    >
                      <i class="fas fa-pencil-alt"></i><span>Editar</span>
                    </li>

                    <li @click="handleView(payment)">
                      <i class="fas fa-eye"></i><span>Visualizar</span>
                    </li>

                    <li
                      v-if="isSys"
                      class="danger-item"
                      :class="{ 'disabled-item': !canDelete() }"
                      @click="canDelete() && handleDelete(payment)"
                    >
                      <i class="fas fa-trash-alt"></i><span>Apagar</span>
                    </li>
                  </ul>
                </div>
              </td>

              <td>
                <span :title="payment.paymentId">{{ payment.paymentId }}</span>
              </td>

              <td class="cell-text-center">
                <span :title="payment.subscriptionId">{{
                  payment.subscriptionId
                }}</span>
              </td>

              <td class="cell-text-center">
                <span :title="payment.userId ?? '—'">
                  {{ payment.userId ?? "—" }}
                </span>
              </td>

              <td class="cell-text-center">
                <span
                  v-if="payment.paymentMethod?.toLowerCase() === 'courtesy'"
                  class="courtesy-method-tag"
                  title="Cortesia"
                >
                  <i class="fas fa-gift"></i><span>Courtesy</span>
                </span>
                <span v-else :title="payment.paymentMethod ?? '—'">
                  {{ payment.paymentMethod ?? "—" }}
                </span>
              </td>

              <td class="cell-text-center">
                <span :title="payment.mpPaymentId ?? '—'">
                  {{ payment.mpPaymentId ?? "—" }}
                </span>
              </td>

              <td>
                <span :class="getPaymentStatusClass(payment.status)">
                  {{ getPaymentStatusLabel(payment.status) }}
                </span>
              </td>

              <td class="cell-text-center">
                <span>{{ Number(payment.baseAmount ?? 0).toFixed(2) }}</span>
              </td>

              <td class="cell-text-center">
                <span>{{ Number(payment.discountAmount ?? 0).toFixed(2) }}</span>
              </td>

              <td class="cell-text-center">
                <span>{{ Number(payment.finalAmount ?? 0).toFixed(2) }}</span>
              </td>

              <td class="cell-text-center">
                <span :title="payment.couponCodeSnapshot ?? '—'">
                  {{ payment.couponCodeSnapshot ?? "—" }}
                </span>
              </td>

              <td>
                <span :title="formatDateTimeForDisplay(payment.createdAt)">
                  {{ formatDateTimeForDisplay(payment.createdAt) }}
                </span>
              </td>

              <td>
                <span :title="payment.statusDetail ?? '—'">
                  {{ payment.statusDetail ?? "—" }}
                </span>
              </td>
            </tr>

            <tr v-if="isLoadingTable" class="empty-row-state">
              <td colspan="14" class="empty-row empty-row-state">
                <div class="empty-state-content">
                  <svg class="empty-spinner" viewBox="0 0 24 24" fill="none">
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                  <p>Carregando payments...</p>
                </div>
              </td>
            </tr>

            <tr
              v-else-if="(paymentStore.totalCount ?? 0) === 0"
              class="empty-row-state"
            >
              <td colspan="14" class="empty-row empty-row-state">
                <div class="empty-state-content">
                  <img src="/img/lupa.webp" alt="" width="38" height="40" class="empty-icon-img" />
                  <p>Nenhum payment encontrado.</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div
          v-if="!isLoadingTable && (paymentStore.totalCount ?? 0) > 0"
          class="sub-pagination-row"
        >
          <div class="sub-pagination-left">
            <span class="sub-pagination-label">Mostrando</span>
            <PaginationInfo
              :current-page="currentPage"
              :page-size="pageSize"
              :total-count="paymentStore.totalCount ?? 0"
            />
            <span>registros</span>
          </div>

          <PaginationButtons
            :current-page="currentPage"
            :page-size="pageSize"
            :total-count="paymentStore.totalCount ?? 0"
            :total-pages="paymentStore.totalPages"
            @change-page="(page) => (currentPage = page)"
          />
        </div>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <PaymentCardModal
      v-if="showCardModal"
      :show="showCardModal"
      :payment-id="selectedCardPaymentId"
      @close="showCardModal = false"
    />
  </Teleport>

  <Teleport to="body">
    <AddPaymentForm
      v-if="showAddModal"
      @close="showAddModal = false"
      @refresh="handlePaymentsChanged({ goToFirstPage: true, closeAdd: true })"
    />

    <EditPaymentForm
      v-if="showEditModal"
      :payment="selectedPayment"
      @close="showEditModal = false"
      @refresh="
        handlePaymentsChanged({ goToFirstPage: false, closeEdit: true })
      "
    />

    <DeletePaymentForm
      v-if="showDeleteModal"
      :payment="selectedPayment"
      @close="showDeleteModal = false"
      @refresh="
        handlePaymentsChanged({
          goToFirstPage: true,
          closeDelete: true,
          clearSelection: true,
        })
      "
    />

    <BatchDeletePaymentForm
      v-if="showBatchDeleteModal"
      :payment-ids="paymentStore.selectedPaymentIds"
      @close="showBatchDeleteModal = false"
      @refresh="
        handlePaymentsChanged({
          goToFirstPage: true,
          closeBatchDelete: true,
          clearSelection: true,
        })
      "
    />
  </Teleport>

  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>
          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            &times;
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
.action-bar-stacked {
  flex-direction: column !important;
  align-items: stretch !important;
}

.cell-text-center {
  text-align: center;
}

.status-tag {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 9999px;
}
.status-green {
  background-color: #b2f2bb;
  color: #2b8a3e;
}
.status-red {
  background-color: #ffc9c9;
  color: #c92a2a;
}
.status-yellow {
  background-color: var(--warning-bg);
  color: #854d0e;
}
.status-purple {
  background-color: #e9d5ff;
  color: #6b21a8;
}
.status-gray {
  background-color: #f1f3f5;
  color: #495057;
}

.sub-pagination-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.5rem 1rem;
  font-size: 0.82rem;
}
.sub-pagination-left {
  display: flex;
  align-items: baseline;
  gap: 4px;
}
.sub-pagination-label {
  padding-top: 2px;
}

.courtesy-method-tag {
  display: inline-flex;
  align-items: center;
  gap: 0;
  padding: 0.2rem 0.6rem;
  font-size: 0.72rem;
  font-weight: 600;
  border-radius: 9999px;
  background-color: var(--brand-soft);
  color: var(--text-on-brand-soft);
  border: 1px solid #c4b5fd;
  white-space: nowrap;
}
.courtesy-method-tag i {
  margin-right: 0.4rem;
}

.empty-state-content {
  width: 100%;
  margin: 0 auto;
}

</style>